import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
  DataCriterion,
  ElasticQueryResult,
  ElasticQueryResultComplete,
  ElasticQuerySearch,
  FilterCriterion,
  FilterCriterionOperator,
  PaginatedPreviewInput,
  PaginatedSearchResponse,
  SortCriterion
} from './elasticquery.interfaces';
import {
  OutputPreviewField,
  PreviewContractReport,
  PreviewFilter,
  PreviewInput,
  TreeFieldTypes
} from './reports.interfaces';

import {ObserveType, ResponseType} from '../app.constants';
import {ApiService} from '../shared/api';
import {FieldType} from '../shared/form-generator/fields/fields.interfaces';
import {DateLocalizationPipe} from '../shared/pipes/date-localization.pipe';
import {FileModel, mapToFileModel} from '../shared/utils/files';

export function toGroupTypeList(value: string | string[]): string[] {
  return Array.isArray(value) ? value : [value];
}

@Injectable()
export class ElasticQueryService {
  public GP_ENTITY_PATH_SUFFIX = '_GP__ENTITY_PATH';

  constructor(private api: ApiService,
              private dateLocalizationPipe: DateLocalizationPipe) {
  }

  public searchFrom(legacySearch: PreviewContractReport): Observable<ElasticQueryResultComplete[]> {
    return this.api.post(`/elasticquery/api/v1/search/contract/info`,
      this.legacySearchToNewSearch(legacySearch));
  }

  public searchFromNewSearch(newSearch: ElasticQuerySearch): Observable<ElasticQueryResultComplete[]> {
    return this.api.post(`/elasticquery/api/v1/search/contract/info`, newSearch);
  }

  public search(document: string, search: ElasticQuerySearch): Observable<ElasticQueryResult[]> {
    return this.api.post(`/elasticquery/api/v1/search/${document}`, search);
  }

  public searchPaginated(
    document: string,
    search: ElasticQuerySearch,
    pageSize = 1000,
    searchAfter?: string | null,
    previousTotalHits?: number | null
  ): Observable<PaginatedSearchResponse> {
    let url = `/elasticquery/api/v1/search/${document}/paginated?pageSize=${pageSize}`;

    if (searchAfter && typeof searchAfter === 'string' && searchAfter.trim().length > 0) {
      const encodedToken = encodeURIComponent(searchAfter);
      url += `&searchAfter=${encodedToken}`;
    }
    if (previousTotalHits != null && previousTotalHits >= 0) {
      url += `&previousTotalHits=${previousTotalHits}`;
    }
    return this.api.post(url, search);
  }

  public export(document: string, search: ElasticQuerySearch): Observable<FileModel> {
    const url = `/elasticquery/api/v1/search/${document}/export`;
    return this.api.post(url, search, {
      requestOptions: {
        observe: ObserveType.RESPONSE,
        responseType: ResponseType.BLOB
      }
    }).pipe(
      map(mapToFileModel())
    );
  }

  public searchFromLegacy(
    legacySearch: PreviewContractReport,
    position: { [key: string]: number },
    entityPathsToDisplay: string[]
  ): Observable<PreviewInput> {

    const elasticQuerySearch =
      this.prepareElasticQuery(legacySearch, entityPathsToDisplay);

    return this.search(legacySearch.filterDocument, elasticQuerySearch).pipe(
      map(response =>
        this.createLegacyResponse(response, position, legacySearch)
      )
    );
  }

  public searchPaginatedFromLegacy(
    legacySearch: PreviewContractReport,
    position: { [key: string]: number },
    entityPathsToDisplay: string[],
    pageSize = 1000,
    searchAfter?: string,
    previousTotalHits?: number | null
  ): Observable<PaginatedPreviewInput> {
    const elasticQuerySearch =
      this.prepareElasticQuery(legacySearch, entityPathsToDisplay);

    return this.searchPaginated(
      legacySearch.filterDocument,
      elasticQuerySearch,
      pageSize,
      searchAfter,
      previousTotalHits
    ).pipe(
      map(response => {
        const legacyResponse = this.createLegacyResponse(
          response.documents,
          position,
          legacySearch
        );
        return {
          ...legacyResponse,
          hasMore: response.hasMore,
          searchAfterString: response.searchAfterString,
          totalHits: response.totalHits
        };
      })
    );
  }

  public exportFromLegacy(
    legacySearch: PreviewContractReport,
    entityPathsToDisplay: string[]
  ): Observable<FileModel> {

    const elasticQuerySearch =
      this.prepareElasticQuery(legacySearch, entityPathsToDisplay);

    return this.export(legacySearch.filterDocument, elasticQuerySearch);
  }

  private prepareElasticQuery(
    legacySearch: PreviewContractReport,
    entityPathsToDisplay: string[]
  ) {
    const elasticQuerySearch = this.legacySearchToNewSearch(legacySearch);

    if (legacySearch.filterDocument === TreeFieldTypes.CONTRACT) {
      elasticQuerySearch.data = this.transformEntityFields(
        elasticQuerySearch.data,
        entityPathsToDisplay
      );

      if (elasticQuerySearch.sort?.length) {
        elasticQuerySearch.sort = this.transformEntityFields(
          elasticQuerySearch.sort,
          entityPathsToDisplay
        );
      }
    }

    return elasticQuerySearch;
  }

  /**
   * Transform fields of type ENTITY to LABEL type with appropriate suffix when they're in the display paths
   */
  private transformEntityFields<T extends { type: string, field: string }>(
    items: T[],
    entityPathsToDisplay: string[]
  ): T[] {
    return items.map(item => {
      if (item.type === FieldType.ENTITY.toString() &&
        entityPathsToDisplay.includes(item.field)) {
        return {
          ...item,
          type: FieldType.LABEL,
          field: item.field + this.GP_ENTITY_PATH_SUFFIX
        };
      }
      return item;
    });
  }

  public getAutoCompleteItems(filterDocument: string, field: string, type: string): Observable<string[]> {
    return this.api.get(`/elasticquery/api/v1/legacy/${filterDocument}/autocomplete?field=${field}&type=${type}`);
  }

  public getAutoCompleteItemsWithTransIds(filterDocument: string, field: string, type: string): Observable<{
    [key: string]: string
  }> {
    return this.api.get(`/elasticquery/api/v1/legacy/${filterDocument}/autocomplete-with-trans-ids?field=${field}&type=${type}`);
  }

  private legacySearchToNewSearch(legacySearch: PreviewContractReport): ElasticQuerySearch {
    return {
      reportName: legacySearch.reportName,
      beginDate: legacySearch.beginDate,
      endDate: legacySearch.endDate,
      data: this.legacyDataToNewData(legacySearch.outputFilters),
      filter: this.legacyFilterToNewFilter(legacySearch.filterParameters),
      sort: this.legacySortToNewSort(legacySearch.outputFilters)
    };
  }

  private legacyDataToNewData(legacyData: OutputPreviewField[]): DataCriterion[] {
    return legacyData.flatMap(legacyDataItem => {
      const groupTypes = toGroupTypeList(legacyDataItem.fieldGroupType ?? []);
      const groupTypeCandidates: (string | undefined)[] = groupTypes.length ? groupTypes : [undefined];
      return legacyDataItem.fieldTypes.flatMap(type =>
        groupTypeCandidates.map(groupType => (<DataCriterion>{
          field: legacyDataItem.field,
          type: type,
          groupType: groupType,
          transIds: legacyDataItem.transIds
        })));
    });
  }

  private legacyFilterToNewFilter(legacyFilters: PreviewFilter[]): FilterCriterion[] {
    const criteria: FilterCriterion[] = [];
    // Operators for which multiple values for the same field/operator should be aggregated
    const aggregatableOperators: FilterCriterionOperator[] = [
      FilterCriterionOperator.EQ,
      FilterCriterionOperator.BEGINS_WITH,
      FilterCriterionOperator.ENDS_WITH,
      FilterCriterionOperator.CONTAINS,
      FilterCriterionOperator.NOT_CONTAINS
    ];

    for (const legacyFilter of legacyFilters) {
      for (const operatorValueItem of legacyFilter.operatorValueList) {
        const isAggregatable = aggregatableOperators.includes(operatorValueItem.operator);
        const existingIndex = criteria.findIndex(addedCriteria =>
          addedCriteria.field === legacyFilter.field && addedCriteria.operator === operatorValueItem.operator
        );

        if (isAggregatable && existingIndex > -1) {
          criteria[existingIndex].values.push(operatorValueItem.value);
        } else {
          criteria.push((<FilterCriterion>{
            field: legacyFilter.field,
            type: legacyFilter.fieldType,
            groupType: toGroupTypeList(legacyFilter.fieldGroupType ?? [])[0],
            values: [operatorValueItem.value],
            operator: operatorValueItem.operator
          }));
        }
      }
    }
    return criteria;
  }

  private legacySortToNewSort(legacySorts: OutputPreviewField[]): SortCriterion[] {
    return legacySorts
      .filter(legacySortItem => !!legacySortItem.fieldSortedBy)
      .sort((legacySortItem1, legacySortItem2) => {
        return legacySortItem1.fieldSortPosition - legacySortItem2.fieldSortPosition;
      })
      .flatMap(legacySortItem => legacySortItem.fieldTypes
        .map(type => (<SortCriterion>{
          field: legacySortItem.field,
          type: type,
          order: legacySortItem.fieldSortedBy.toUpperCase()
        })));
  }

  private createLegacyResponse(response: any, position: any, legacySearch: PreviewContractReport): PreviewInput {
    return {
      position: position,
      contractIds: response.map(field => field.contractId),
      result: this.createLegacyResult(response, legacySearch)
    };
  }

  public createLegacyResult(response: any, legacySearch: PreviewContractReport): any {
    return response.map(field => {
      const legacyField = {};
      if (legacySearch.beginDate !== legacySearch.endDate) {
        legacyField['beginDate'] = this.dateLocalizationPipe.transform(field.beginDate);
        legacyField['endDate'] = this.dateLocalizationPipe.transform(field.endDate);
      }
      Object.keys(field).forEach(key => {
        const notResultKeys = ['id', 'cliCoreId', 'employeeId', 'contractId', 'beginDate', 'endDate'];
        if (!notResultKeys.includes(key)) {
          if (key === 'payValues') {
            field[key].forEach(payValue => {
              const newKey = payValue.name;
              const payValueOutputFilter = legacySearch.outputFilters.find(outputFilter => outputFilter.field === newKey);
              legacyField[newKey] = payValue.sumValue;
              if (payValueOutputFilter.displayRetro) {
                const retroKey = newKey + ' - Retroactivity';
                legacyField[retroKey] = payValue.retroValue;
              }
            });
          } else if (key === 'payPeriod') {
            legacyField['beginDate'] = this.dateLocalizationPipe.transform(field[key].beginDate);
            legacyField['endDate'] = this.dateLocalizationPipe.transform(field[key].endDate);
          } else if (key === 'calculationDate') {
            legacyField['calculationDate'] = this.dateLocalizationPipe.transform(field[key]);
          } else if (key === 'external') {
            legacyField['external'] = field[key];
          } else if (Array.isArray(field[key])) {
            legacyField[key] = field[key].map(val => {
              return val.transId || val.value;
            });
            legacyField[key] = legacyField[key].length === 1 ? legacyField[key][0] : legacyField[key];
          } else {
            legacyField[key] = field[key].transId || field[key].value;
          }
        }
      });
      return legacyField;
    });
  }
}
